import React, { ReactElement, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';

// Month picker with the familiar month grid, but the year is changed with ◀ ▶ arrows instead of the
// native input's scroll-through. `value`/`onChange` use the "YYYY-MM" format ('' = nothing chosen).
interface MonthPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MonthPicker = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select month',
}: MonthPickerProps): ReactElement => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const has = Boolean(value);
  const [year, monthNo] = has ? value.split('-').map(Number) : [new Date().getFullYear(), 0];
  const selectedMonth = has ? monthNo - 1 : -1;
  const [viewYear, setViewYear] = useState(year);

  const monthShort = (i: number): string =>
    new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(new Date(2020, i, 1));
  const label = has
    ? new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(
        new Date(year, selectedMonth, 1),
      )
    : placeholder;

  const pick = (i: number): void => {
    onChange(`${viewYear}-${String(i + 1).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        if (next) setViewYear(year); // start each open on the selected year
        setOpen(next);
      }}
    >
      <PopoverTrigger
        type="button"
        disabled={disabled}
        className="flex h-8 w-[12rem] items-center justify-between gap-2 rounded-lg border border-input bg-popover px-3 text-left text-sm text-foreground shadow-xs outline-none transition-colors hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-popover"
      >
        <span className={cx('capitalize', !has && 'text-muted-foreground')}>{label}</span>
        <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" align="start">
        <div className="mb-1.5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setViewYear(y => y - 1)}
            aria-label={String(viewYear - 1)}
            className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-semibold text-foreground">{viewYear}</span>
          <button
            type="button"
            onClick={() => setViewYear(y => y + 1)}
            aria-label={String(viewYear + 1)}
            className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => {
            const active = i === selectedMonth && viewYear === year;
            return (
              <button
                key={monthShort(i)}
                type="button"
                onClick={() => pick(i)}
                className={cx(
                  'rounded-md py-1.5 text-sm capitalize transition-colors',
                  active ? 'bg-primary font-medium text-primary-foreground' : 'text-foreground hover:bg-muted',
                )}
              >
                {monthShort(i)}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MonthPicker;
